import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Avatar,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Skeleton,
  LinearProgress,
  Badge,
  Chip,
  Checkbox,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Person,
  Business,
  CloudUpload,
  CheckCircle,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth, UserProfile } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useSetupGuide } from '../context/SetupGuideContext';
import useFirstTimeUser from '../hooks/useFirstTimeUser';
import { useLocation } from 'react-router-dom';
import { InvoiceService } from '../services/invoiceService';
import { BusinessData } from '../types/business';
import InPageNavSidebar, { InPageSection } from '../components/common/InPageNavSidebar';
import { isValidGSTN } from '../utils/gstHelpers';

const validationSchema = Yup.object({
  displayName: Yup.string().required('Name is required'),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit phone number')
    .required('Phone number is required'),
  businessName: Yup.string().required('Business name is required'),
  gstRegistered: Yup.string().oneOf(['registered', 'not_registered'], 'Please select GST registration status'),
  gstType: Yup.string().when('gstRegistered', (gstRegistered, schema) => {
    return (Array.isArray(gstRegistered) ? gstRegistered[0] : gstRegistered) === 'registered'
      ? schema.oneOf(['regular', 'composition'], 'Please select GST type')
             .required('GST type is required')
      : schema.notRequired();
  }),
  gstn: Yup.string()
    .when('gstRegistered', (gstRegistered, schema) => {
      return (Array.isArray(gstRegistered) ? gstRegistered[0] : gstRegistered) === 'registered'
        ? schema
            .required('GSTN is required when registered under GST')
            .test('gstn-format', 'Enter a valid GSTN (Format: 22AAAAA0000A1Z5)', function(value) {
              if (!value) return false;
              return isValidGSTN(value);
            })
        : schema.notRequired();
    }),
  businessActivityType: Yup.string()
    .required('Business activity type is required')
    .oneOf(['Manufacturing', 'Trading', 'Services', 'Social Media'], 'Please select a valid business activity type'),
  serviceType: Yup.string().when('businessActivityType', (businessActivityType, schema) => {
    return (Array.isArray(businessActivityType) ? businessActivityType[0] : businessActivityType) === 'Services'
      ? schema.required('Please specify the type of service')
      : schema.notRequired();
  }),
  manufacturingActivities: Yup.array().when('businessActivityType', (businessActivityType, schema) => {
    return (Array.isArray(businessActivityType) ? businessActivityType[0] : businessActivityType) === 'Manufacturing'
      ? schema.min(1, 'Please specify at least one manufacturing activity')
      : schema.notRequired();
  }),
  tradingType: Yup.string().when('businessActivityType', (businessActivityType, schema) => {
    return (Array.isArray(businessActivityType) ? businessActivityType[0] : businessActivityType) === 'Trading'
      ? schema.required('Please specify the type of trading')
      : schema.notRequired();
  }),
  socialMediaType: Yup.string().when('businessActivityType', (businessActivityType, schema) => {
    return (Array.isArray(businessActivityType) ? businessActivityType[0] : businessActivityType) === 'Social Media'
      ? schema.required('Please specify the type of social media business')
      : schema.notRequired();
  }),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  pincode: Yup.string()
    .matches(/^[1-9][0-9]{5}$/, 'Enter a valid 6-digit pincode')
    .required('Pincode is required'),
  role: Yup.string().required('Role is required'),
});

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const manufacturingOptions = [
  'Textiles and Apparel', 'Food and Beverages', 'Chemicals and Pharmaceuticals',
  'Electronics and Electrical', 'Automotive', 'Machinery and Equipment',
  'Construction Materials', 'Paper and Printing', 'Plastics and Rubber',
  'Metals and Metal Products', 'Furniture and Wood Products', 'Other Manufacturing'
];

const tradingOptions = [
  'Wholesale Trading', 'Retail Trading', 'Import/Export', 'E-commerce',
  'Commodity Trading', 'Electronics Trading', 'Textile Trading',
  'Automotive Trading', 'Agricultural Products', 'Other Trading'
];

const serviceOptions = [
  'IT & Software', 'Financial Services', 'Legal Services', 'Consulting', 
  'Healthcare', 'Education & Training', 'Hospitality', 'Transportation', 
  'Real Estate', 'Marketing & Advertising', 'Design Services', 'Entertainment',
  'Repairs & Maintenance', 'Other Services'
];

const socialMediaOptions = [
  'Content Creation', 'Influencer Marketing', 'Social Media Management',
  'Digital Marketing', 'Affiliate Marketing', 'Other Social Media'
];

const Profile: React.FC = () => {
  const { userProfile, updateUserProfile } = useAuth();
  const { checkSetupStatus, isSetupComplete } = useSetupGuide();
  const { isFirstTimeUser } = useFirstTimeUser();
  const location = useLocation();
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [isVerifyingUpdate, setIsVerifyingUpdate] = useState(false);

  // Check if we should enter edit mode (from router location state)
  useEffect(() => {
    if (location.state && (location.state as any).activeTab !== undefined) {
      // Always enter edit mode since we only have business tab
      setEditMode(true);
      // Clear the state after handling it
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const toggleEditMode = () => {
    if (editMode) {
      formik.resetForm();
    }
    setEditMode(!editMode);
  };

  // Update useEffect for profile completion calculation
  useEffect(() => {
    if (userProfile) {
      const requiredFields = [
        'displayName', 'businessName', 'businessType', 'businessActivityType',
        'address', 'city', 'state', 'pincode', 'phone', 'role'
      ];
      
      const completedFields = requiredFields.filter(field => {
        const value = userProfile[field as keyof UserProfile];
        return value && String(value).trim() !== '';
      });
      
      const completionPercentage = Math.round((completedFields.length / requiredFields.length) * 100);
      setProfileCompletion(completionPercentage);
      
      const missing = requiredFields.filter(field => {
        const value = userProfile[field as keyof UserProfile];
        return !value || String(value).trim() === '';
      });
      setMissingFields(missing);
    }
  }, [userProfile]);

  const formik = useFormik({
    initialValues: {
      displayName: userProfile?.displayName || '',
      phone: userProfile?.phone || '',
      businessName: userProfile?.businessName || '',
      businessType: userProfile?.businessType || '',
      gstRegistered: userProfile?.gstRegistered || 'not_registered',
      gstType: userProfile?.gstType || '',
      gstn: userProfile?.gstn || '',
      businessActivityType: userProfile?.businessActivityType || '',
      manufacturingActivities: userProfile?.manufacturingActivities || [],
      tradingType: userProfile?.tradingType || '',
      serviceType: userProfile?.serviceType || '',
      socialMediaType: userProfile?.socialMediaType || '',
      address: userProfile?.address || '',
      city: userProfile?.city || '',
      state: userProfile?.state || '',
      pincode: userProfile?.pincode || '',
      role: userProfile?.role || 'admin',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // Business data only
        const businessData: Partial<UserProfile> = {
          displayName: values.displayName.trim(),
          businessName: values.businessName.trim(),
          businessType: values.businessType,
          gstRegistered: values.gstRegistered as 'registered' | 'not_registered',
          gstType: values.gstType as '' | 'regular' | 'composition',
          gstn: values.gstn ? values.gstn.trim() : null,
          address: values.address.trim(),
          city: values.city.trim(),
          state: values.state,
          pincode: values.pincode.trim(),
          phone: values.phone.trim(),
          role: values.role as 'admin' | 'user',
          businessActivityType: values.businessActivityType,
          manufacturingActivities: values.businessActivityType === 'Manufacturing' ? values.manufacturingActivities : [],
          tradingType: values.businessActivityType === 'Trading' ? values.tradingType : '',
          serviceType: values.businessActivityType === 'Services' ? values.serviceType : '',
          socialMediaType: values.businessActivityType === 'Social Media' ? values.socialMediaType : '',
        };

        const dataToUpdate = businessData;
        
        // Validate required business fields
        if (!businessData.businessName) {
          setSnackbar({
            open: true,
            message: 'Business name is required',
            severity: 'error',
          });
          setIsLoading(false);
          return;
        }
        
        if (!businessData.address) {
          setSnackbar({
            open: true,
            message: 'Business address is required',
            severity: 'error',
          });
          setIsLoading(false);
          return;
        }
        
        await updateUserProfile(dataToUpdate);
        
        // Re-check setup status after profile update
        checkSetupStatus();
        
        // Verify the business data was saved correctly
        if (userProfile?.uid) {
          await verifyBusinessData(userProfile.uid);
        } else {
          setSnackbar({
            open: true,
            message: 'Business profile updated successfully',
            severity: 'success',
          });
        }
        
        setEditMode(false);
      } catch (error) {
        console.error('Error updating profile:', error);
        setSnackbar({
          open: true,
          message: 'Failed to update business profile. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const verifyBusinessData = async (uid: string) => {
    setIsVerifyingUpdate(true);
    try {
      // Simulate verification delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSnackbar({
        open: true,
        message: 'Business profile updated and verified successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error verifying business data:', error);
      setSnackbar({
        open: true,
        message: 'Profile updated but verification failed. Please refresh the page.',
        severity: 'warning',
      });
    } finally {
      setIsVerifyingUpdate(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    }
  };

  if (!userProfile) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 4 } }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  const sections: InPageSection[] = [
    { id: 'business-profile', label: 'Business Profile' },
    { id: 'document-uploads', label: 'Document Uploads' },
    { id: 'profile-completion', label: 'Profile Completion' },
  ];

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 4 } }}>
      {/* Fixed Sidebar */}
      <InPageNavSidebar sections={sections} offsetTop={96} />
      
      <Box sx={{ 
        ml: { xs: 0, lg: '320px' },
        px: { xs: 2, sm: 3, md: 4 },
        maxWidth: { xs: '100%', lg: 'calc(100% - 320px)' },
        width: { xs: '100%', lg: 'calc(100% - 320px)' }
      }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
        <Box mb={4}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            My Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your business information
          </Typography>
        </Box>

      {/* Add profile completion information but only show it for users that haven't completed setup */}
      {profileCompletion < 100 && !isSetupComplete && (isFirstTimeUser || editMode) && (
        <motion.div variants={itemVariants}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
          >
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Complete your business profile to get started
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You're {profileCompletion}% complete. Please fill in the missing information below.
            </Typography>
            {missingFields.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Missing: {missingFields.join(', ')}
                </Typography>
              </Box>
            )}
          </Alert>
        </motion.div>
      )}

        <Box>
      <Paper sx={{ borderRadius: 3, mb: 4 }} id="business-profile">
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Business />
          <Typography variant="h6" fontWeight="medium">
            BUSINESS PROFILE
          </Typography>
        </Box>

        <motion.div variants={itemVariants}>
          <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" p={3}>
            <Box display="flex" alignItems="center">
              <Box sx={{ position: 'relative' }}>
                <Avatar 
                  src={userProfile?.photoURL || undefined} 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Business sx={{ fontSize: 40 }} />
                </Avatar>
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box ml={2}>
                <Typography variant="h5" fontWeight="bold">
                  {userProfile?.displayName || 'User Name'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userProfile?.email || 'user@example.com'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userProfile?.businessName || 'Business Name'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant={editMode ? "contained" : "outlined"}
              color={editMode ? "primary" : "secondary"}
              startIcon={editMode ? <SaveIcon /> : <EditIcon />}
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                if (editMode) {
                  formik.handleSubmit();
                } else {
                  toggleEditMode();
                }
              }}
              disabled={isLoading}
              sx={{ 
                borderRadius: 2,
                boxShadow: editMode ? '0 4px 10px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              {editMode ? 'Save Changes' : 'Edit Business Profile'}
            </Button>
          </Box>

          <AnimatePresence>
            {editMode && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3, 
                    mx: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': { alignItems: 'center' }
                  }}
                >
                  You are now in edit mode. Make your changes and save them.
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="displayName"
                    name="displayName"
                    label="Full Name"
                    value={formik.values.displayName}
                    onChange={formik.handleChange}
                    error={formik.touched.displayName && Boolean(formik.errors.displayName)}
                    helperText={formik.touched.displayName && formik.errors.displayName ? String(formik.errors.displayName) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone ? String(formik.errors.phone) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="businessName"
                    name="businessName"
                    label="Business Name"
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                    helperText={formik.touched.businessName && formik.errors.businessName ? String(formik.errors.businessName) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="business-type-label">Business Type</InputLabel>
                    <Select
                      labelId="business-type-label"
                      id="businessType"
                      name="businessType"
                      value={formik.values.businessType}
                      onChange={formik.handleChange}
                      label="Business Type"
                      disabled={!editMode || isLoading}
                    >
                      <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                      <MenuItem value="Partnership">Partnership</MenuItem>
                      <MenuItem value="Private Limited">Private Limited</MenuItem>
                      <MenuItem value="Public Limited">Public Limited</MenuItem>
                      <MenuItem value="LLP">Limited Liability Partnership</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="gst-registered-label">GST Registration Status</InputLabel>
                    <Select
                      labelId="gst-registered-label"
                      id="gstRegistered"
                      name="gstRegistered"
                      value={formik.values.gstRegistered}
                      onChange={(e) => {
                        // Clear gstType and gstn if not registered
                        if (e.target.value === 'not_registered') {
                          formik.setFieldValue('gstType', '');
                          formik.setFieldValue('gstn', '');
                        }
                        formik.handleChange(e);
                      }}
                      label="GST Registration Status"
                      disabled={!editMode || isLoading}
                    >
                      <MenuItem value="registered">Registered</MenuItem>
                      <MenuItem value="not_registered">Not Registered</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formik.values.gstRegistered === 'registered' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="gst-type-label">GST Type</InputLabel>
                      <Select
                        labelId="gst-type-label"
                        id="gstType"
                        name="gstType"
                        value={formik.values.gstType}
                        onChange={formik.handleChange}
                        label="GST Type"
                        disabled={!editMode || isLoading}
                      >
                        <MenuItem value="regular">Regular</MenuItem>
                        <MenuItem value="composition">Composition Scheme</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {formik.values.gstRegistered === 'registered' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="gstn"
                      name="gstn"
                      label="GSTN"
                      value={formik.values.gstn}
                      onChange={formik.handleChange}
                      error={formik.touched.gstn && Boolean(formik.errors.gstn)}
                      helperText={formik.touched.gstn && formik.errors.gstn ? String(formik.errors.gstn) : undefined}
                      disabled={!editMode || isLoading}
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="business-activity-label">Business Activity Type</InputLabel>
                    <Select
                      labelId="business-activity-label"
                      id="businessActivityType"
                      name="businessActivityType"
                      value={formik.values.businessActivityType}
                      onChange={(e) => {
                        // Clear dependent fields when changing activity type
                        formik.setFieldValue('manufacturingActivities', []);
                        formik.setFieldValue('tradingType', '');
                        formik.setFieldValue('serviceType', '');
                        formik.setFieldValue('socialMediaType', '');
                        formik.handleChange(e);
                      }}
                      label="Business Activity Type"
                      disabled={!editMode || isLoading}
                    >
                      <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                      <MenuItem value="Trading">Trading</MenuItem>
                      <MenuItem value="Services">Services</MenuItem>
                      <MenuItem value="Social Media">Social Media</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formik.values.businessActivityType === 'Manufacturing' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="manufacturing-activities-label">Manufacturing Activities</InputLabel>
                      <Select
                        labelId="manufacturing-activities-label"
                        id="manufacturingActivities"
                        name="manufacturingActivities"
                        multiple
                        value={formik.values.manufacturingActivities}
                        onChange={formik.handleChange}
                        label="Manufacturing Activities"
                        disabled={!editMode || isLoading}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {manufacturingOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            <Checkbox checked={formik.values.manufacturingActivities.indexOf(option) > -1} />
                            <ListItemText primary={option} />
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.manufacturingActivities && formik.errors.manufacturingActivities && (
                        <FormHelperText error>
                          {String(formik.errors.manufacturingActivities)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                {formik.values.businessActivityType === 'Trading' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="trading-type-label">Trading Type</InputLabel>
                      <Select
                        labelId="trading-type-label"
                        id="tradingType"
                        name="tradingType"
                        value={formik.values.tradingType}
                        onChange={formik.handleChange}
                        label="Trading Type"
                        disabled={!editMode || isLoading}
                      >
                        {tradingOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.tradingType && formik.errors.tradingType && (
                        <FormHelperText error>
                          {String(formik.errors.tradingType)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                {formik.values.businessActivityType === 'Services' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="service-type-label">Service Type</InputLabel>
                      <Select
                        labelId="service-type-label"
                        id="serviceType"
                        name="serviceType"
                        value={formik.values.serviceType}
                        onChange={formik.handleChange}
                        label="Service Type"
                        disabled={!editMode || isLoading}
                      >
                        {serviceOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.serviceType && formik.errors.serviceType && (
                        <FormHelperText error>
                          {String(formik.errors.serviceType)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                {formik.values.businessActivityType === 'Social Media' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel id="social-media-type-label">Social Media Type</InputLabel>
                      <Select
                        labelId="social-media-type-label"
                        id="socialMediaType"
                        name="socialMediaType"
                        value={formik.values.socialMediaType}
                        onChange={formik.handleChange}
                        label="Social Media Type"
                        disabled={!editMode || isLoading}
                      >
                        {socialMediaOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.socialMediaType && formik.errors.socialMediaType && (
                        <FormHelperText error>
                          {String(formik.errors.socialMediaType)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    label="Business Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address ? String(formik.errors.address) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    id="city"
                    name="city"
                    label="City"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city ? String(formik.errors.city) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="state-label">State</InputLabel>
                    <Select
                      labelId="state-label"
                      id="state"
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      label="State"
                      disabled={!editMode || isLoading}
                    >
                      {indianStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.state && formik.errors.state && (
                      <FormHelperText error>
                        {String(formik.errors.state)}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    id="pincode"
                    name="pincode"
                    label="Pincode"
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                    helperText={formik.touched.pincode && formik.errors.pincode ? String(formik.errors.pincode) : undefined}
                    disabled={!editMode || isLoading}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      labelId="role-label"
                      id="role"
                      name="role"
                      value={formik.values.role}
                      onChange={formik.handleChange}
                      label="Role"
                      disabled={!editMode || isLoading}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="Accountant">Accountant</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </form>
        </motion.div>
      </Paper>

      <Grid container spacing={3} id="document-uploads">
        <Grid item xs={12} md={6}>
          <motion.div 
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <Card 
              component={motion.div}
              variants={cardVariants}
              whileHover="hover"
              sx={{ borderRadius: 3, height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <Business />
                  </Avatar>
                  <Typography variant="h6" fontWeight="medium">
                    Document Uploads
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Box 
                  sx={{ 
                    border: '2px dashed #ccc', 
                    borderRadius: 2, 
                    p: 3, 
                    textAlign: 'center',
                    mb: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(0, 0, 0, 0.01)'
                    }
                  }}
                >
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Upload business documents
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag and drop files here or click to browse
                  </Typography>
                </Box>
                
                <Button 
                  variant="outlined" 
                  fullWidth 
                  startIcon={<CloudUpload />}
                  sx={{ borderRadius: 2 }}
                >
                  Upload Documents
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} md={6} id="profile-completion">
          <motion.div 
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <Card 
              component={motion.div}
              variants={cardVariants}
              whileHover="hover"
              sx={{ borderRadius: 3, height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                    <CheckCircle />
                  </Avatar>
                  <Typography variant="h6" fontWeight="medium">
                    Profile Completion
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Profile Progress
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {profileCompletion}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={profileCompletion} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        bgcolor: profileCompletion === 100 ? 'success.main' : 'primary.main'
                      }
                    }} 
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Missing Information:
                  </Typography>
                  {missingFields.length > 0 ? (
                    <Box>
                      {missingFields.map((field, index) => (
                        <Chip 
                          key={index}
                          label={field} 
                          size="small" 
                          color="warning" 
                          sx={{ mr: 1, mb: 1 }} 
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      ✓ All required information is complete
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Profile;